import { observable, action, computed } from 'mobx';
import Store, { INVENTORY, IOpeningHours } from './Store';
import StoresCollection from './StoresCollection';
import {
  convertDateToIsoString,
  getDateFromTimeZone,
  getOffsetDate,
} from '../../../utils/TimeUtils';

export interface ICourierTimesData {
  dropoffDeadlineTime: string;
  dropoffReadyTime: string;
  pickupDeadlineTime: string;
  pickupReadyTime: string;
}

export interface IHours {
  [key: string]: number;
}

const TODAY = 'today';

// using MAX_RADIUS hardcoded until BE changes merged on all branches URLS
const MAX_RADIUS = 15;

/* Time interval calculations for Same Day Delivery (courier):
  Options are available for today and next business day depending on
  hours of the closest available store and the current time in the store's timezone.
  See https://confluence.esteeonline.com/display/OM/Postmates+Dropoff+windows+and+Scheduling+Flow */

class CourierStoresCollection extends StoresCollection {
  @observable isAddressUndeliverable = false;

  @action
  setIsAddressUndeliverable = (success: boolean): void => {
    this.isAddressUndeliverable = success;
  };

  @computed
  get userTimezoneOffset(): number {
    return (new Date().getTimezoneOffset() * -1) / 60;
  }

  @computed
  get userTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  @computed
  get isUserInStoreTimeZone(): boolean {
    return this.userTimeZone === this.courierStore?.timeZone;
  }

  @computed
  get timezoneOffsetDate(): Date {
    return (
      this.isUserInStoreTimeZone
        ? new Date()
        : getDateFromTimeZone(this.courierStore?.timeZone)
    );
  }

  @computed
  get todayDate(): string {
    return this.timezoneOffsetDate.toLocaleString('en-US', { day: 'numeric', month: 'long' });
  }

  @computed
  get currentTime(): number {
    const date = this.timezoneOffsetDate;
    return date.getMinutes() > 0
      ? Number(`${date.getHours() + 1}00`)
      : Number(`${date.getHours()}00`);
  }

  @computed
  get today(): number {
    const date = this.timezoneOffsetDate;
    return date.getDay();
  }

  @computed
  get nextBusinessDayDate(): string {
    return new Date(
      this.timezoneOffsetDate.getTime() + 24 * this.dayDiff * 60 * 60 * 1000,
    ).toLocaleString('en-US', {
      day: 'numeric',
      month: 'long',
    });
  }

  calculatePickUpTimeOffset = (date: Date): Date => {
    date.setSeconds(-600);
    return date;
  };

  @computed
  get courierStore(): Store {
    const courierableStore = this.stores
     && this.stores.find(({ isCourierSelected }) => isCourierSelected);
    return courierableStore || this.closestStore;
  }

  @computed
  get validCourierStores(): Store[] {
    return (
      this.stores?.filter((store: Store) => (
        (store.itemsAvailable === INVENTORY.ALL || store.itemsAvailable === INVENTORY.PARTIAL)
          && store.isStoreOpen
          && store.isCourierActive
          && store.distance < (store.courierRadius ? store.courierRadius : MAX_RADIUS)
      )));
  }

  @computed
  get courierStoresSortedByDistance(): Store[] {
    return (this.validCourierStores?.sort((firstStore, secondStore) => (
      firstStore.distance - secondStore.distance)));
  }

  @computed
  get courierStoresSortedByAvailability(): Store[] {
    const ITEMS_SORT_ORDER = [INVENTORY.ALL, INVENTORY.PARTIAL];
    return (this.courierStoresSortedByDistance?.sort((firstStore, secondStore) => (
      ITEMS_SORT_ORDER.indexOf(firstStore.itemsAvailable) - ITEMS_SORT_ORDER.indexOf(secondStore.itemsAvailable))));
  }

  @computed
  get closestStore(): Store {
    return this.courierStoresSortedByAvailability?.[0];
  }

  @computed
  get sortedDoorIds(): string {
    return this.courierStoresSortedByAvailability?.map(s => s.doorId).join();
  }

  getTodaysHoursByDay = (day: number): IOpeningHours => (
    this.courierStore?.openingHours?.find((s: IOpeningHours) => s.day === day)
  );

  @computed
  get nextBusinessDay(): number {
    const tomorrow = this.today < 6 ? this.today + 1 : 0;
    for (let i = 0; i <= 6; i += 1) {
      // Start looping from tomorrow to find next open day.
      const tomorrowPointer = (i + tomorrow) % 7;
      const nextDayHours = this.getTodaysHoursByDay(tomorrowPointer);
      const isOpen = nextDayHours
        && !!nextDayHours.closedHours
        && !!nextDayHours.openingHours;
      if (isOpen) {
        return tomorrowPointer;
      }
    }
    return tomorrow;
  }

  @computed
  get dayDiff(): number {
    const diff = this.today < this.nextBusinessDay
      ? this.nextBusinessDay - this.today
      : (this.nextBusinessDay + 7) - this.today;
    return diff;
  }

  @computed
  get courierStoreTodaysHours(): IOpeningHours {
    return this.getTodaysHoursByDay(this.today);
  }

  @computed
  get courierStoreNextBusinessDayHours(): IOpeningHours {
    return this.getTodaysHoursByDay(this.nextBusinessDay);
  }

  @computed
  get isBeforeOpening(): boolean {
    return this.courierStoreTodaysHours?.openingHours > this.currentTime;
  }

  @computed
  get isDuringHours(): boolean {
    return this.courierStoreTodaysHours?.openingHours <= this.currentTime
      && this.currentTime < (this.courierStoreTodaysHours.closedHours);
  }

  @computed
  get isAfterHours(): boolean {
    return this.courierStoreTodaysHours?.closedHours <= this.currentTime;
  }

  @computed
  get sameDayLastOrder(): number {
    return this.courierStoreTodaysHours?.closedHours
      && this.courierStoreTodaysHours.closedHours - 300;
  }

  @computed
  get isCourierStoreNotAvailable(): boolean {
    return Boolean(!this.courierStore);
  }

  getCourierValsFromTimeRange = (val: string): ICourierTimesData => {
    let date;
    const splitVal = val.split('-');
    if (splitVal[0].indexOf('today') !== -1) {
      date = this.timezoneOffsetDate.toLocaleDateString('en-US');
    } else if (splitVal[0].indexOf('tomorrow') !== -1) {
      date = new Date(this.timezoneOffsetDate.getTime() + 24 * this.dayDiff * 60 * 60 * 1000).toLocaleDateString('en-US');
    }
    const offset = this.courierStore?.timeZoneOffsetHours;
    const dropoffDeadline = getOffsetDate({ date, offset, time: splitVal[2] });
    const dropoffReady = getOffsetDate({ date, offset, time: splitVal[1] });
    return {
      dropoffDeadlineTime: convertDateToIsoString(dropoffDeadline),
      dropoffReadyTime: convertDateToIsoString(dropoffReady),
      pickupDeadlineTime: convertDateToIsoString(this.calculatePickUpTimeOffset(dropoffDeadline)),
      pickupReadyTime: convertDateToIsoString(this.calculatePickUpTimeOffset(dropoffReady)),
    };
  };

  getCourierValsFromFirstOption = (): string => {
    const todayArray = this.getHoursArray('today');
    const tomorrowArray = this.getHoursArray('tomorrow');
    if (!this.isAfterHours && todayArray && todayArray[0]) {
      return `today-${todayArray[0].start}-${todayArray[0].end}`;
    }
    if (tomorrowArray && tomorrowArray[0]) {
      return `tomorrow-${tomorrowArray[0].start}-${tomorrowArray[0].end}`;
    }
    return '';
  };

  getHoursArray = (day: string): IHours[] => {
    const {
      courierStoreTodaysHours,
      courierStoreNextBusinessDayHours,
      currentTime,
      isDuringHours,
    } = this;
    const todayArray = [];
    if (courierStoreTodaysHours && courierStoreNextBusinessDayHours) {
      const {
        openingHours: todaysOpeningHours,
        closedHours: todaysClosedHours,
      } = courierStoreTodaysHours;
      const {
        openingHours: tomorrowOpeningHours,
        closedHours: tomorrowClosedHours,
      } = courierStoreNextBusinessDayHours;
      const openingHours = day === TODAY ? todaysOpeningHours : tomorrowOpeningHours;
      const closedHours = day === TODAY ? todaysClosedHours : tomorrowClosedHours;
      // Adjust midnight closing time for calculation
      const adjustedClosedHours = closedHours === 0 ? 2400 : closedHours;
      const optionsStartTime = isDuringHours
        && day === TODAY
        && currentTime < (adjustedClosedHours - 300)
        ? currentTime + 200 // two hour buffer after opening if isDuringHours
        : isDuringHours && day === TODAY && currentTime >= (adjustedClosedHours - 300)
          ? currentTime
          : openingHours + 100; // one hour buffer after opening
      const optionsEndTime = isDuringHours
        && day === TODAY
        && adjustedClosedHours - currentTime < 300
        ? adjustedClosedHours - 300 // last order 3 hours before store close
        : adjustedClosedHours;
      for (let i = optionsStartTime; i < optionsEndTime; i += 200) {
        const end = i + 200;
        if (end <= optionsEndTime) {
          todayArray.push({ end, start: i });
        }
        // Ranges should be 2 hours. Leave out odd remaining hour.
        if (end === optionsEndTime || optionsEndTime - end === 100) break;
      }
    }
    return todayArray;
  };

  @computed
  get shouldShowCourierError(): boolean {
    return (
      ((this.courierStore && !this.isStoreWithinRadius)
      || this.courierStore?.hasInvalidStoreHours(this.today)
      || this.courierStore?.hasInvalidStoreHours(this.nextBusinessDay)
      || this.isCourierStoreNotAvailable
      || this.isAddressUndeliverable)
    );
  }

  @computed
  get isStoreWithinRadius(): boolean {
    return this.courierStore
      && this.courierStore.distance < (this.courierStore.courierRadius
        ? this.courierStore.courierRadius : MAX_RADIUS);
  }
}

export default CourierStoresCollection;
